/**
 * Database tab component.
 *
 * Displays RDS PostgreSQL database health and metrics.
 *
 * Implementation plan reference: D2.17 (excluding D2.17b schema card)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import svgPanZoom from 'svg-pan-zoom';
import { ApiError, getDatabase, getSchemaErd } from '@/api/client';
import { formatDateTime } from '@/utils/format';
import { useRefresh } from '@/context/RefreshContext';
import { MiniBar } from '@/components/MiniBar';
import { Badge } from '@/components/Badge';
import type { DatabaseHealth, DatabaseResponse, Environment, SchemaErdReport } from '@/types/api';

interface DatabaseTabProps {
  environment: Environment;
}

function healthBadgeVariant(health: DatabaseHealth): 'success' | 'warning' | 'error' | 'neutral' {
  switch (health) {
    case 'healthy':
      return 'success';
    case 'warning':
      return 'warning';
    case 'critical':
      return 'error';
    default:
      return 'neutral';
  }
}

const formatWhen = (iso: string): string =>
  formatDateTime(iso, { dateStyle: 'medium', timeStyle: 'short' });

export function DatabaseTab({ environment }: DatabaseTabProps): JSX.Element {
  const { manualRefreshNonce, tabVisible } = useRefresh();

  // Database health state
  const [data, setData] = useState<DatabaseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Schema ERD state (environment-independent)
  const [schemaReport, setSchemaReport] = useState<SchemaErdReport | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const panZoomRef = useRef<SvgPanZoom.Instance | null>(null);

  useEffect(() => {
    if (!tabVisible) {
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setData(null);

    void getDatabase(environment)
      .then((response) => {
        if (!cancelled) {
          setData(response);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError) {
          setError(`Failed to load database data (${err.status}).`);
        } else {
          setError('Failed to load database data.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [environment, manualRefreshNonce, tabVisible]);

  // Schema fetch — environment-independent, runs once when tab becomes visible
  useEffect(() => {
    if (!tabVisible) {
      return undefined;
    }

    let cancelled = false;
    setSchemaLoading(true);
    setSchemaError(null);
    setSvgContent(null);

    void getSchemaErd()
      .then(async (response) => {
        if (cancelled) return;
        const report = response.reports[0] ?? null;
        setSchemaReport(report);

        if (report?.svgUrl == null) {
          setSchemaError(report === null ? 'No schema snapshots available.' : 'SVG not yet available for this snapshot.');
          setSchemaLoading(false);
          return;
        }

        // Fetch the SVG content via the presigned URL (no auth header needed)
        const svgRes = await fetch(report.svgUrl);
        if (!svgRes.ok) throw new Error(`SVG fetch failed: ${svgRes.status}`);
        const svgText = await svgRes.text();
        if (!cancelled) {
          setSvgContent(svgText);
          setSchemaLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSchemaError(err instanceof Error ? err.message : 'Failed to load schema.');
        setSchemaLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tabVisible, manualRefreshNonce]);

  // Inject SVG text into the container once it's mounted (after schemaLoading → false)
  useEffect(() => {
    if (svgContent === null || svgContainerRef.current === null) return;
    svgContainerRef.current.innerHTML = svgContent;
  }, [svgContent]);

  // Initialise svg-pan-zoom once SVG content is injected into the container
  useEffect(() => {
    if (schemaLoading || schemaError !== null || svgContainerRef.current === null) {
      return undefined;
    }

    const svgEl = svgContainerRef.current.querySelector('svg');
    if (svgEl === null) return undefined;

    // Fill the container
    svgEl.style.width = '100%';
    svgEl.style.height = '100%';

    panZoomRef.current?.destroy();
    panZoomRef.current = svgPanZoom(svgEl, {
      zoomEnabled: true,
      panEnabled: true,
      controlIconsEnabled: false,
      fit: true,
      center: true,
      minZoom: 0.05,
      maxZoom: 20,
      zoomScaleSensitivity: 0.3,
    });

    return () => {
      panZoomRef.current?.destroy();
      panZoomRef.current = null;
    };
  }, [schemaLoading, schemaError]);

  const handleZoomIn = useCallback(() => panZoomRef.current?.zoomIn(), []);
  const handleZoomOut = useCallback(() => panZoomRef.current?.zoomOut(), []);
  const handleFit = useCallback(() => {
    panZoomRef.current?.fit();
    panZoomRef.current?.center();
  }, []);

  return (
    <div className="database-tab">
      <h2>Database Health - {environment.toUpperCase()}</h2>
      <p className="tab-lead">RDS instance and CloudWatch metrics for the selected environment.</p>

      {isLoading && <p className="placeholder">Loading database data...</p>}
      {!isLoading && error !== null && <p className="placeholder">{error}</p>}
      {!isLoading && error === null && data !== null && (
        <>
          <p className="fetched-at">Last updated {formatWhen(data.fetchedAt)}</p>

          <div className="database-cards">
            <article className="info-card">
              <h3 className="info-card-title">Instance</h3>
              <dl className="info-dl">

                <div>
                  <dt>Identifier</dt>
                  <dd>{data.instance.identifier}</dd>
                </div>
                <div>
                  <dt>Engine</dt>
                  <dd>
                    {data.instance.engine} {data.instance.engineVersion}
                  </dd>
                </div>
                <div>
                  <dt>Class</dt>
                  <dd>{data.instance.instanceClass}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{data.instance.status}</dd>
                </div>
                <div>
                  <dt>Multi-AZ</dt>
                  <dd>{data.instance.multiAZ ? 'Yes' : 'No'}</dd>
                </div>
                <div>
                  <dt>Storage</dt>
                  <dd>
                    {data.instance.storageType} · {data.instance.allocatedStorageGB} GB
                  </dd>
                </div>
              </dl>
            </article>

            <article className="info-card">
              <h3 className="info-card-title">Utilisation &amp; latency</h3>
              <div className="info-card-badges">
                <Badge variant={healthBadgeVariant(data.metrics.health)}>{data.metrics.health}</Badge>
              </div>
              <div className="database-metrics-bars">
                <MiniBar
                  value={Math.round(data.metrics.cpuPercent * 10) / 10}
                  label="CPU"
                  variant={
                    data.metrics.cpuPercent >= 90 ? 'error' : data.metrics.cpuPercent >= 70 ? 'warning' : 'default'
                  }
                />
                <div className="metric-row">
                  <span className="metric-label">Connections</span>
                  <span className="metric-value">
                    {data.metrics.connectionCount} / {data.metrics.maxConnections}
                  </span>
                </div>
                <MiniBar
                  value={Math.round((100 - data.metrics.freeStoragePercent) * 10) / 10}
                  label="Storage"
                  variant={
                    data.metrics.freeStoragePercent <= 10
                      ? 'error'
                      : data.metrics.freeStoragePercent <= 20
                        ? 'warning'
                        : 'default'
                  }
                />
                <div className="metric-row">
                  <span className="metric-label">Read latency</span>
                  <span className="metric-value">{data.metrics.readLatencyMs} ms</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Write latency</span>
                  <span className="metric-value">{data.metrics.writeLatencyMs} ms</span>
                </div>
              </div>
            </article>
          </div>
        </>
      )}

      <section className="schema-erd-section" aria-label="Schema ERD">
        <div className="schema-erd-header">
          <h3 className="schema-erd-title">Schema ERD</h3>
          {schemaReport !== null && (
            <span className="schema-erd-meta">
              {schemaReport.reportDate.slice(0, 4)}-{schemaReport.reportDate.slice(4, 6)}-{schemaReport.reportDate.slice(6, 8)}
              {' · '}
              <code>{schemaReport.shortSha}</code>
              {schemaReport.lastUpdated !== null && (
                <> · {formatWhen(schemaReport.lastUpdated)}</>
              )}
            </span>
          )}
          {!schemaLoading && schemaError === null && (
            <div className="schema-erd-controls" role="group" aria-label="Diagram zoom controls">
              <button type="button" className="schema-zoom-btn" onClick={handleZoomIn} aria-label="Zoom in">+</button>
              <button type="button" className="schema-zoom-btn" onClick={handleZoomOut} aria-label="Zoom out">−</button>
              <button type="button" className="schema-zoom-btn" onClick={handleFit} aria-label="Fit diagram to view">Fit</button>
              {schemaReport?.schemaUrl != null && (
                <a
                  href={schemaReport.schemaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="schema-download-link"
                >
                  .mmd
                </a>
              )}
            </div>
          )}
        </div>
        <p className="schema-erd-note">
          Environment-independent — reflects the Liquibase changelog state at the commit above.
        </p>
        {schemaLoading && <p className="placeholder">Loading schema diagram...</p>}
        {!schemaLoading && schemaError !== null && (
          <p className="placeholder">{schemaError}</p>
        )}
        {!schemaLoading && schemaError === null && (
          <div
            ref={svgContainerRef}
            className="schema-erd-viewer"
            aria-label="Interactive schema diagram — scroll or pinch to zoom, drag to pan"
          />
        )}
      </section>
    </div>
  );
}
